/**
 * Rename Chat Dialog
 *
 * Modal dialog to rename a chat session.
 * Features inline editing with validation and keyboard shortcuts.
 */

import { Pencil, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../utils/cn';
import { Dialog } from './dialog';
import { Button } from './button';

type RenameChatDialogProps = {
  isOpen: boolean;
  currentTitle: string;
  onConfirm: (newTitle: string) => void;
  onCancel: () => void;
  isRenaming?: boolean;
};

export const RenameChatDialog: React.FC<RenameChatDialogProps> = ({
  isOpen,
  currentTitle,
  onConfirm,
  onCancel,
  isRenaming = false,
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset title when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTitle(currentTitle);
      setError(null);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [isOpen, currentTitle]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      const trimmedTitle = title.trim();

      if (!trimmedTitle) {
        setError('Title cannot be empty');
        return;
      }

      if (trimmedTitle.length > 200) {
        setError('Title must be 200 characters or less');
        return;
      }

      if (trimmedTitle === currentTitle) {
        onCancel();
        return;
      }

      onConfirm(trimmedTitle);
    },
    [title, currentTitle, onConfirm, onCancel]
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onCancel}
      size="md"
      disableClose={isRenaming}
      aria-labelledby="rename-dialog-title"
      className="p-6"
    >
      {/* Close button */}
      <button
        onClick={onCancel}
        disabled={isRenaming}
        className={cn(
          'absolute top-4 right-4 p-1.5 rounded-md',
          'text-muted-foreground hover:text-foreground',
          'hover:bg-accent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        aria-label="Close dialog"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Icon */}
      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-primary/10">
        <Pencil className="w-5 h-5 text-primary" />
      </div>

      {/* Title */}
      <h2
        id="rename-dialog-title"
        className="text-lg font-semibold text-center text-foreground mb-4"
      >
        Rename Chat
      </h2>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
              setError(null);
            }}
            disabled={isRenaming}
            placeholder="Enter chat title..."
            className={cn(
              'w-full px-4 py-3 rounded-lg text-sm',
              'bg-muted/50 border border-border',
              'focus:border-primary focus:bg-background focus:outline-none',
              'transition-all duration-200',
              'placeholder:text-muted-foreground/60',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error && 'border-destructive focus:border-destructive'
            )}
            maxLength={200}
          />
          <div className="mt-2 flex items-center justify-between min-h-[16px]">
            <span className="text-xs text-destructive">{error ?? ''}</span>
            <span className={cn(
              'text-xs tabular-nums',
              title.length >= 190 ? 'text-destructive font-medium' :
              title.length >= 160 ? 'text-amber-500' :
              'text-muted-foreground'
            )}>
              {title.trim() === currentTitle.trim()
                ? <span className="text-muted-foreground/60 italic">unchanged</span>
                : `${title.length}/200`
              }
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 w-full justify-between">
          <Button
            type="button"
            onClick={onCancel}
            disabled={isRenaming}
            variant="secondary"
            size="md"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isRenaming || !title.trim() || title.trim() === currentTitle.trim()} variant="primary" size="md">
            {isRenaming ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};

export default RenameChatDialog;
